import React from "react";
import classnames from "classnames";
import styles from "./user-management-header.module.scss";
import { TuvisSkeleton } from "@whatslly/ui-components/TuvisSkeleton";
import { Button } from "@whatslly/ui-components/v2/Button";
import { SearchInput } from "@whatslly/ui-components/v2/BaseElements/SearchInput";
import { useTranslation } from "react-i18next";
import type { UserManagementUser } from "../../types/UserManagementUser";
import { useDependencyInjector } from "@whatslly/commons/di";
import { UserManagementStore } from "../../stores/UserManagementStore";
import { observer } from "mobx-react-lite";
import { HeaderButtonsIcons } from "../HeaderButtonsIcons";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { AuthorizedAction } from "@whatslly/ui-components/AuthorizedAction";
import { EditColumns } from "@whatslly/ui-components/EditColumns/EditColumns";
import type { SupportedSidecarChannel } from "@whatslly/commons/sidecarUtils";
import type { PermissionInstanceConfigs } from "@whatslly/commons/platformPermissions";
export const UserManagementHeader: React.FC<{
  loading?: boolean;
  allowInvite?: boolean;
  error?: string;
  users: UserManagementUser[];
  extraButtons?: React.ReactNode;
  shouldCheckAuthorizedAction?: boolean;
  canCreateSidecar: boolean;
  availableChannelsForCreation: { label: string; value: SupportedSidecarChannel }[];
  instanceConfigs?: PermissionInstanceConfigs;
  findDuplicates?: { onClick: () => void; tooltip: string; loading?: boolean };
}> = observer(
  ({
    loading,
    error,
    users,
    allowInvite,
    extraButtons,
    shouldCheckAuthorizedAction = false,
    canCreateSidecar,
    availableChannelsForCreation,
    instanceConfigs,
    findDuplicates,
  }) => {
    const { t } = useTranslation();

    const store = useDependencyInjector(UserManagementStore);

    return (
      <>
        <div className={classnames(styles.header, { [styles.headerWithError]: error })}>
          {!error && (
            <TuvisSkeleton width={"200px"} height={"40px"} loading={loading}>
              <div className={styles.headerButtons}>
                <EditColumns className={styles.headerButton} t={t} />
                <div className={styles.headerButtonsSeparator} />
                <HeaderButtonsIcons users={users} instanceConfigs={instanceConfigs} findDuplicates={findDuplicates} />
                <div className={styles.headerButtonsSeparator} />
                <div className={styles.subHeaderContainer}>
                  <div className={classnames(styles.searchBarContainer, { [styles.searchInputLoading]: loading })}>
                    <SearchInput
                      data-testid={`users-search-input`}
                      className={styles.searchInput}
                      isDisabled={loading}
                      clearAllContainerClassName={styles.clearAllContainer}
                      value={store.searchTerm}
                      placeholder={t("user_management.search_users_placeholder")}
                      onChange={(newSearchTerm) => store.setSearchTerm(newSearchTerm)}
                    />
                  </div>
                </div>
                <div className={styles.headerButtonsSeparator} />
                {extraButtons}
                {allowInvite &&
                  (shouldCheckAuthorizedAction ? (
                    <AuthorizedAction permission={UserPermissions["user_management.invite"]}>
                      <Button
                        data-testid={`invite-button`}
                        onClick={() => store.inviteUsers(canCreateSidecar, availableChannelsForCreation, instanceConfigs)}
                        svg={{ iconName: `plus`, width: 13, height: 13 }}
                        paddingInline={"small"}
                        text={t("user_management.invite_users")}
                      />
                    </AuthorizedAction>
                  ) : (
                    <Button
                      data-testid={`invite-button`}
                      onClick={() => store.inviteUsers(canCreateSidecar, availableChannelsForCreation, instanceConfigs)}
                      svg={{ iconName: `plus`, width: 13, height: 13 }}
                      paddingInline={"small"}
                      text={t("user_management.invite_users")}
                    />
                  ))}
              </div>
            </TuvisSkeleton>
          )}
        </div>
      </>
    );
  }
);
